import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  AbilityEffect,
  Gift,
  CharacterSheetInterface,
  Potence,
  CharacterGift,
  Receivable,
  ReceivableItem,
} from "@dndModels";
import { SmartHash } from "@models";

import API from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import LoadingBox from "@components/input/LoadingBox";
import DandDButtonBox from "@dndComponents/DandDButtonBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";

interface Props {
  char: Character;
  effect: AbilityEffect;
  potence: Potence;
  disabled?: boolean;
  shared: boolean;
  onChange: () => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterSelfGiftEffect({
  char,
  effect,
  potence,
  disabled = false,
  shared,
  onChange,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [ping, setPing] = useState(false);
  const [selected_id, setSelectedId] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        // margin: "4px",
        backgroundColor: theme.palette.secondary.main
      },
    });
  });

   
  useEffect(() => {
    if (!fetched) {
      load();
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
     
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id)));
    
  const load = () => {
    setLoading(true);
    setFetched(true);
    const onComplete = (res: SmartHash) => {
      setGifts(res.gift as Gift[]);
      setLoading(false);
    };
    const onProgress = () => {};
    api.getSetOfObjectsWithCallbacks({ 
      data_types: ["gift"],
      onComplete, onProgress 
    });
  }

  const { classes, cx } = useStyles();

  const renderCharacters = (gift: Gift, level: number) => {
    const campaign = char.campaign;
    if (campaign) {
      return campaign.characters.filter(o => o.active && !o.is_archived && o._id !== char._id).map(char => {
        return (
          <Grid key={char._id} size={{
            xs: 12
          }}>
            <div className="padded">
              <Grid container spacing={0} direction="row" className={cx(classes.root, "padded")}>
                <Grid
                  size={{
                    xs: 9
                  }}>
                  { char.name }
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <DandDButtonBox 
                    name="Apply"
                    disabled={processing}
                    onClick={() => {
                      const receivable = new Receivable();
                      receivable.from_id = char._id;
                      receivable.char_id = char._id;
                      receivable.sent_dt = common_util.get_time_number();
                      receivable.origin_id = api.temp_id;
                      receivable.type = "Mail";
                      receivable.items = [new ReceivableItem({
                        type: "Gift",
                        obj_id: gift._id,
                        count: 1,
                      })];
                      setProcessing(true);
                      api.upsertObject("receivable", receivable).then(res => {
                        setProcessing(false);
                      });
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        );
      });
    }
    return null;
  }
  
  const renderExtras = () => {
    const self_condition_ids = potence.conditions_applied;
    let gift: Gift | undefined = undefined;
    let level = 1;
    if (self_condition_ids.length === 1) {
      gift = gifts.find(o => o._id === self_condition_ids[0]);
      if (gift) {
        if (gift.max_level > 1) {
          level = char_util.value(potence.condition_levels_applied, { obj: char });
        } 
        if (potence.remove_condition_levels) {
          level = -1 * level;
        }
      } else {
        return <>Invalid Condition</>;
      }
    } else {
      gift = gifts.find(o => o._id === selected_id);
    }
    if (gift && level !== 0) {
      return (
        <ModalOrDrawer 
          open={ open } 
          full_page
          onClose={() => {
            setOpen(false);
            char.subscription_holder.modalClosed();
          }}>
          { open &&
            <Grid container spacing={0} direction="row" 
              style={{ maxHeight: window.innerHeight - 34 }}>
              <Grid
                className="header centered"
                size={{
                  xs: 12
                }}>
                { level === 1 ? 
                  `Add ${gift.name} to Allies`
                : level === -1 ? 
                  `Remove ${gift.name} from Allies`
                : level < 0 ? 
                  `Remove ${level * -1} levels of ${gift.name} from Allies`
                :
                  `Add ${level} levels of ${gift.name} to Allies`
                }
              </Grid>
              { renderCharacters(gift, level) }
            </Grid>
          }
        </ModalOrDrawer>
      );
    }
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const self_gift_ids = potence.conditions_applied;
    // const range_string = char_util.getRangeString({ char_int: char, spell, effect });
              
    if (self_gift_ids.length === 0) {
      return null;
    } else if (self_gift_ids.length === 1) {
      const gift = gifts.find(o => o._id === self_gift_ids[0]);
      if (gift) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              className="centered"
              size={{
                xs: 4
              }}>
              Self
            </Grid>
            <Grid
              className="centered"
              size={{
                xs: 4
              }}>
              
            </Grid>
            <Grid
              className="centered"
              style={{ height: "37.5" }}
              size={{
                xs: 4
              }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DandDButtonBox
                    name={ char.spirit.gifts.filter(o => o.source_id === self_gift_ids[0]).length > 0 ? 
                      `Remove ${gift.name}` : 
                      `Apply ${gift.name}` 
                    }
                    disabled={ !enabled }
                    onClick={ async () => {
                      const gift_id = potence.conditions_applied[0];
                      const char_gift = char.spirit.gifts.find(o => o.source_id === gift_id);
                      if (char_gift) {
                        char_util.removeCharFeatureBasesFor(char, char_gift);
                        char.spirit.gifts = char.spirit.gifts.filter(o => o.source_id !== char_gift.source_id);
                        char.mind.spells = char.mind.spells.filter(o => o.source_type !== "Gift" || o.source_id !== char_gift.source_id);
                        // onChange();
                      } else {
                        // Apply the gift
                        const obj = gifts.find(o => o._id === gift_id);
                        if (obj) {
                          await api.loadObject(obj);
                          const char_gift = new CharacterGift();
                          await char_gift.copy_source({ char: char, feature_source: obj });
                          char.spirit.gifts.push(char_gift);
                          
                          // onChange();
                          // setPing(!ping);
                        }
                      }
                      char.changed();
                      char.subscription_holder.recalculate();
                      setPing(!ping);
                    }} 
                  />
                </Grid>
                { effect.target_int.target_type !== "Self" && char.campaign &&
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <DandDButtonBox
                      name="Apply to Ally"
                      disabled={ !enabled }
                      onClick={() => {
                        setOpen(true);
                      }}
                    />
                    { renderExtras() }
                  </Grid>
                }
              </Grid>
            </Grid>
          </Grid>
        );
      } else {
        return <Grid>Invalid Gift</Grid>;
      }
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            className="centered"
            size={{
              xs: 4
            }}>
            { effect.name }
          </Grid>
          <Grid
            className="centered"
            size={{
              xs: 4
            }}>
            <SelectObjectBox 
              name="Gift"
              data_type="gift"
              filter={{ _id: { "in": self_gift_ids }}}
              value={selected_id}
              onChange={setSelectedId}
            />
          </Grid>
          <Grid
            className="centered"
            size={{
              xs: 4
            }}>
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }}>
                <DandDButtonBox
                  name={ 
                    selected_id === "" ? 
                      "First Select a Gift" : 
                      (char.spirit.gifts.filter(o => o.source_id === selected_id).length > 0 ? 
                        "Remove" : 
                        "Apply"
                      ) 
                  }
                  style={{
                    fontSize: "9px",
                  }}
                  disabled={ !enabled || selected_id === "" }
                  onClick={ async () => {
                    const gift_id = selected_id;
                    const char_gift = char.spirit.gifts.find(o => o.source_id === gift_id);
                    if (char_gift) {
                      char_util.removeCharFeatureBasesFor(char, char_gift);
                      char.spirit.gifts = char.spirit.gifts.filter(o => o.source_id !== char_gift.source_id);
                      char.mind.spells = char.mind.spells.filter(o => o.source_type !== "Gift" || o.source_id !== char_gift.source_id);
                      onChange();
                      setPing(!ping);
                    } else {
                      // Apply the gift
                      const obj = gifts.find(o => o._id === gift_id);
                      if (obj) {
                        await api.loadObject(obj);
                        const char_gift = new CharacterGift();
                        await char_gift.copy_source({ char: char, feature_source: obj });
                        char.spirit.gifts.push(char_gift);
                        
                        onChange();
                        setPing(!ping);
                      }
                    }
                  }} 
                />
              </Grid>
              { effect.target_int.target_type !== "Self" && (char.minions.length > 0 || char.campaign) &&
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DandDButtonBox
                    name="Apply to Ally"
                    disabled={ !enabled || selected_id === "" }
                    onClick={() => {
                      setOpen(true);
                    }}
                  />
                  { renderExtras() }
                </Grid>
              }
            </Grid>
          </Grid>
        </Grid>
      );
    }
  }
}
